import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { setAuth, setUser } from '../../redux/authSlice';
import { usePageHeader } from '../../context/PageHeaderContext';
import { btnPrimarySx, btnDisabledSx } from '../../utils/buttonSx';
import { useTimeSettings } from '../../hooks/useTimeSettings';
import { getTheme } from '../../theme/themes';

interface TopbarProps { onMenuClick?: () => void; themeId?: string; onThemeChange?: (id: string) => void; }

const PAGE_KEYS: Record<string, string> = {
  '/reports':   'nav.reports',
  '/dashboard': 'nav.dashboard',
  '/devices':   'nav.devices',
  '/zones':     'nav.zones',
  '/companies': 'nav.companies',
  '/schedule':  'nav.schedule',
  '/log':       'nav.log',
  '/settings':  'nav.settings',
  '/screens':   'nav.screens',
  '/media':     'nav.media',
  '/old':       'nav.old',
};

const Topbar: React.FC<TopbarProps> = ({ onMenuClick, themeId, onThemeChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s: RootState) => s.auth.user);
  const [clock, setClock] = useState('');
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const timeSettings = useTimeSettings();
  const { breadcrumbs, saveConfig, actionConfig } = usePageHeader();

  const isDark = themeId ? getTheme(themeId).mode === 'dark' : false;
  const toggleTheme = () => onThemeChange?.(isDark ? 'sky' : 'navy');

  const logout = () => {
    dispatch(setAuth(false));
    dispatch(setUser(null));
    localStorage.removeItem('pv-token');
  };

  const pageKey = Object.keys(PAGE_KEYS).find(k => location.pathname === k || location.pathname.startsWith(k + '/'));
  const pageTitle = t(PAGE_KEYS[pageKey || ''] || 'nav.devices');

  useEffect(() => {
    const tick = () => {
      setClock(new Date().toLocaleTimeString('uk-UA', {
        timeZone: timeSettings.timezone,
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timeSettings.timezone]);

  return (
    <>
    <Box sx={{
      height: 52, borderBottom: '1px solid var(--divider)',
      background: 'var(--topbar-bg)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      display: 'flex', alignItems: 'center',
      px: { xs: 1.5, md: 3 }, gap: 1.5, flexShrink: 0,
      position: 'sticky', top: 0, zIndex: 100,
    }}>

      {/* Hamburger — mobile only */}
      {onMenuClick && (
        <IconButton size="small" onClick={onMenuClick} sx={{ color: 'var(--text-secondary)', fontSize: 18, mr: 0.5 }}>☰</IconButton>
      )}

      {/* Back button — when inside a subsection (2+ breadcrumb segments) */}
      {breadcrumbs.length > 1 && (() => {
        const parentSeg = breadcrumbs[breadcrumbs.length - 2];
        return (
          <Box
            onClick={parentSeg.onClick}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
              border: '1px solid var(--glass-border)',
              background: 'var(--glass-bg)',
              cursor: 'pointer', color: 'var(--text-secondary)',
              transition: 'all .15s',
              '&:hover': { bgcolor: 'var(--selected-bg)', borderColor: 'var(--accent)', color: 'var(--accent-light)', transform: 'translateX(-1px)' },
            }}
          >
            <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
              <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        );
      })()}

      {/* Title / breadcrumbs */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: '5px', minWidth: 0, overflow: 'hidden' }}>
        {breadcrumbs.length > 0 ? (
          breadcrumbs.map((seg, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Typography sx={{ fontSize: 15, color: 'var(--text-disabled)', opacity: .45, flexShrink: 0, lineHeight: 1.3 }}>›</Typography>}
              <Typography
                onClick={seg.onClick}
                sx={{
                  fontSize: 15,
                  fontWeight: i === breadcrumbs.length - 1 ? 600 : 400,
                  color: i === breadcrumbs.length - 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                  cursor: seg.onClick ? 'pointer' : 'default',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3,
                  transition: 'color .12s',
                  '&:hover': seg.onClick ? { color: 'var(--accent-light)' } : {},
                  flexShrink: i < breadcrumbs.length - 1 ? 0 : 1,
                }}
              >
                {seg.label}
              </Typography>
            </React.Fragment>
          ))
        ) : (
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
            {pageTitle}
          </Typography>
        )}
      </Box>

      {/* Clock */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-secondary)', letterSpacing: 0.5, display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}>
        {clock}
      </Typography>

      {/* Save button — appears when any page registers unsaved changes */}
      {/* Primary page action (mobile: the create button lives here, not in the page) */}
      {actionConfig && (
        <Box
          component="button"
          onClick={actionConfig.disabled ? undefined : actionConfig.onClick}
          sx={{ ...btnPrimarySx, ...(actionConfig.disabled ? btnDisabledSx : {}), flexShrink: 0 }}
        >
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {actionConfig.label}
        </Box>
      )}

      {saveConfig && (() => {
        const canSave = (saveConfig.enabled ?? saveConfig.dirty) && !saveConfig.saving;
        return (
        <Box
          onClick={canSave ? saveConfig.onSave : undefined}
          title={saveConfig.hint || ''}
          sx={{
            px: '14px', height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center',
            fontSize: 13, fontWeight: 600, flexShrink: 0, cursor: canSave ? 'pointer' : 'default',
            background: saveConfig.saved
              ? 'rgba(16,185,129,.15)'
              : saveConfig.dirty
              ? 'var(--accent)'
              : 'var(--glass-hover)',
            color: saveConfig.saved ? '#34d399' : saveConfig.dirty ? '#fff' : 'var(--text-disabled)',
            border: saveConfig.saved
              ? '1px solid rgba(16,185,129,.3)'
              : saveConfig.dirty
              ? 'none'
              : '1px solid var(--glass-border)',
            boxShadow: saveConfig.dirty && !saveConfig.saved ? '0 2px 8px var(--accent-glow)' : 'none',
            transition: 'all .2s',
            userSelect: 'none',
            opacity: canSave || saveConfig.saved ? 1 : .55,
            '&:hover': canSave ? { filter: 'brightness(1.08)' } : {},
          }}
        >
          {saveConfig.saved ? '✓ Saved' : saveConfig.saving ? 'Saving…' : 'Save'}
        </Box>
        );
      })()}

      {/* Dark / Light mode toggle */}
      {onThemeChange && (
        <Box
          onClick={toggleTheme}
          title={isDark ? 'Light mode' : 'Dark mode'}
          sx={{
            width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
            border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, cursor: 'pointer', transition: 'all .15s',
            '&:hover': { bgcolor: 'var(--glass-hover)', borderColor: 'var(--accent)' },
          }}
        >
          {isDark ? '☀️' : '🌙'}
        </Box>
      )}

      {/* Account avatar — desktop only */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
        <Box
          onClick={e => setAccountAnchor(e.currentTarget)}
          sx={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, var(--accent), var(--accent-light))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
            border: '2px solid transparent',
            transition: 'border-color .15s',
            '&:hover': { borderColor: 'var(--accent-light)' },
          }}
        >
          {user?.login?.[0]?.toUpperCase() || 'A'}
        </Box>
        <Menu
          anchorEl={accountAnchor}
          open={Boolean(accountAnchor)}
          onClose={() => setAccountAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: { mt: '6px', minWidth: 190, borderRadius: '12px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)' } } }}
        >
          <Box sx={{ px: '14px', py: '10px', borderBottom: '1px solid var(--glass-border)' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{user?.login || 'user'}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{user?.isAdmin ? t('user.admin') : t('user.operator')}</Typography>
          </Box>
          <MenuItem onClick={() => { setAccountAnchor(null); navigate('/profile'); }} sx={{ fontSize: 14, gap: '10px', color: 'var(--text-secondary)', py: '9px' }}>👤 {t('nav.profile')}</MenuItem>
          <Box sx={{ my: '4px', mx: '8px', borderTop: '1px solid var(--glass-border)' }} />
          <MenuItem onClick={() => { setAccountAnchor(null); logout(); }} sx={{ fontSize: 14, gap: '10px', color: '#f87171', py: '9px' }}>↪ {t('topbar.logout')}</MenuItem>
        </Menu>
      </Box>

    </Box>
    </>
  );
};

export default Topbar;
